import moment from "moment";
import { get, isEmpty } from "lodash";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import localStorageService from "service/localStorageService";
import { convertSecondsToPrometheusTime, clsToAttributeTable } from "utils/functions";

// OBJ values that should always be treated as sensors, regardless of
// the generic hex‑prefix heuristic below. This includes:
//  - 32784 / 32800: standard LDSBus sensor classes
//  - 40961: Virtual Motion / similar virtual binary sensors
//  - 40962: Virtual Water sensor / similar virtual binary sensors
const SENSOR_OBJ_VALUES = new Set([32784, 32800, 40961, 40962]);

const ACTUATOR_OBJ_VALUES = new Set([16401, 33024]);

const METER_OBJ_VALUES = new Set([32801, 32803, 32802]);

const parseObjValue = (objId: string | number): number => {
    if (objId === null || objId === undefined) return Number.NaN;
    const objStr = String(objId).trim();
    if (objStr.startsWith("0x") || objStr.startsWith("0X")) {
        return Number.parseInt(objStr, 16);
    }
    const decimalValue = Number.parseInt(objStr, 10);
    if (Number.isNaN(decimalValue)) {
        return Number.parseInt(objStr, 16);
    }
    return decimalValue;
};

/**
 * Normalizes human-readable entity labels (e.g. tag names, device NAME/NICK)
 * so that small formatting differences like spaces vs underscores do not matter.
 *
 * Examples:
 *  - "Room_1"  -> "room1"
 *  - "Room 1"  -> "room1"
 *  - " room-1 " -> "room1"
 */
export const normalizeEntityLabel = (
    value: string | null | undefined
): string => {
    if (!value) return "";
    return String(value)
        .toLowerCase()
        // Remove all non‑alphanumeric characters (spaces, underscores, hyphens, etc.)
        .replaceAll(/[^a-z0-9]/g, "");
};

/**
 * Build a UID -> device lookup map from registry devices.
 */
export const buildUIDToDeviceMap = (devices: any[]): Map<string, any> => {
    const uidToDeviceMap = new Map<string, any>();
    for (const device of devices || []) {
        const uid = String(device?.UID ?? device?.uid ?? "");
        if (uid) {
            uidToDeviceMap.set(uid, device);
        }
    }
    return uidToDeviceMap;
};

/**
 * Returns true when the tag name matches at least one matched registry device
 * by NAME or NICK after normalization.
 */
export const hasMatchingRegistryNameOrNick = (
    tagName: string,
    tagUIDs: Set<string>,
    registryUIDs: Set<string>,
    uidToDeviceMap: Map<string, any>
): boolean => {
    const normalizedTagName = normalizeEntityLabel(tagName);
    if (!normalizedTagName) {
        return false;
    }

    for (const uid of Array.from(tagUIDs)) {
        if (!registryUIDs.has(uid)) {
            continue;
        }
        const device = uidToDeviceMap.get(uid);
        if (!device) {
            continue;
        }
        const normalizedName = normalizeEntityLabel(device?.NAME ?? device?.name ?? "");
        const normalizedNick = normalizeEntityLabel(device?.NICK ?? device?.nick ?? "");
        if (normalizedTagName === normalizedName || normalizedTagName === normalizedNick) {
            return true;
        }
    }

    return false;
};

export const isAuxiliaryHub = (objId: string | number): boolean => {
    const numericValue = parseObjValue(objId);
    return !Number.isNaN(numericValue); 
};

export const isSensor = (objId: string | number): boolean => {
    const numericValue = parseObjValue(objId);
    if (Number.isNaN(numericValue)) return false;
    if (isAuxiliaryHub(numericValue)) return false;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (METER_OBJ_VALUES.has(numericValue)) return false;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (SENSOR_OBJ_VALUES.has(numericValue)) return true;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    const hex = numericValue.toString(16).toUpperCase();
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    return hex.startsWith("8");
};

export const isMeter = (objId: string | number): boolean => {
    const numericValue = parseObjValue(objId);
    return !Number.isNaN(numericValue) && METER_OBJ_VALUES.has(numericValue);
};

export const containsActuatorObject = (objId: string | number): boolean => {
    const numericValue = parseObjValue(objId);
    if (Number.isNaN(numericValue)) return false;
    // IO Controller (OBJ 49153) should be treated as an actuator source
    // even though it also appears in AUXILIARY_HUB_OBJ_VALUES.
    if (numericValue === 49153) return true;
    if (isAuxiliaryHub(numericValue)) return false;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (ACTUATOR_OBJ_VALUES.has(numericValue)) return true;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    const hex = numericValue.toString(16).toUpperCase();
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    const first = hex[0];
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    return first === "4" || first === "C";
};

export const isRoomOrZoneByName = (name: string | null | undefined): boolean => {
    if (!name || typeof name !== "string") return false;
    const nameLower = name.toLowerCase().trim();
    return nameLower === "room" || nameLower === "zone";
};

export const isRoomByName = (name: string | null | undefined, nick?: string | null | undefined): boolean => {
    const nameLower = name ? String(name).toLowerCase().trim() : "";
    const nickLower = nick ? String(nick).toLowerCase().trim() : "";
    return nameLower === "room" || nickLower === "room";
};

export const isGroupOrHubByName = (name: string | null | undefined): boolean => {
    if (!name || typeof name !== "string") return false;
    const nameLower = name.toLowerCase().trim();
    return nameLower === "group" || nameLower === "hub";
};

export const isEntityDevice = (device: any): boolean => {
    const name = device?.NAME;
    return isRoomOrZoneByName(name) || isGroupOrHubByName(name);
};

/**
 * Returns true if the sensor belongs to a zone/room/group/hub entity device (by NAME or NICK).
 * Used in networks to exclude "Binary Digital Input" and similar entries that come from
 * entity devices rather than actual physical sensors.
 */
export const isSensorFromEntityDevice = (sensor: any, devices: any[]): boolean => {
    const uid = sensor?.ldsu_uuid ?? sensor?.ldsuUuid ?? "";
    if (!uid) return false;
    const device = devices.find(
        (d: any) => String(d?.UID ?? d?.uid ?? "") === String(uid)
    );
    if (!device) return false;
    const name = device?.NAME;
    const nick = device?.NICK;
    return (
        isRoomOrZoneByName(name) ||
        isRoomOrZoneByName(nick) ||
        isGroupOrHubByName(name) ||
        isGroupOrHubByName(nick)
    );
};

/**
 * Exclude only entity devices (room/zone/group/hub by NAME or OBJ 45057/45058).
 * Do NOT exclude auxiliary hub devices (e.g. LDSBus 4in1 Sensor OBJ 32768) so that
 * sensors from them show in room/group/zone/hub when tagged (e.g. room4in1).
 */
export const shouldExcludeDeviceFromEntityList = (device: any): boolean => {
    if (!device) return false;
    const name = device?.NAME;
    const nick = device?.NICK;
    if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) return true;
    if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) return true;
    const obj = device?.OBJ;
    return obj === "45057" || obj === 45057 || obj === "45058" || obj === 45058;
};

/**
 * Count sensor instances that belong to a tag (same logic as Room Details filterRoomSensors).
 */
export const countSensorInstancesByTag = (
    sensors: any[],
    tagUIDs: Set<string>,
    tagUIDSAIDs: Map<string, Set<number>>,
    uidToDeviceMap: Map<string, any>
): number => {
    return sensors.filter((sensor: any) => {
        const uid = sensor.ldsu_uuid || sensor.UID || "";
        const said = sensor.SAID ? Number(sensor.SAID) : 0;
        if (!tagUIDs.has(uid)) return false;
        const allowed = tagUIDSAIDs.get(uid);
        if (allowed && !allowed.has(said)) return false;
        const device = uidToDeviceMap.get(uid);
        return !shouldExcludeDeviceFromEntityList(device);
    }).length;
};

/**
 * Count actuator instances that belong to a tag (same logic as Room Details filterRoomActuators).
 */
export const countActuatorInstancesByTag = (
    actuators: any[],
    tagUIDs: Set<string>,
    tagUIDSAIDs: Map<string, Set<number>>,
    uidToDeviceMap: Map<string, any>
): number => {
    return actuators.filter((actuator: any) => {
        const uid = actuator.UID || "";
        const said = actuator.said ? Number(actuator.said) : 0;
        if (!tagUIDs.has(uid)) return false;
        const allowed = tagUIDSAIDs.get(uid);
        if (allowed && allowed.size > 0 && !allowed.has(said)) return false;
        const device = uidToDeviceMap.get(uid);
        return !shouldExcludeDeviceFromEntityList(device);
    }).length;
};

/**
 * Count sensor and actuator instances that belong to a tag (same logic as Room Details
 * filterRoomSensors/filterRoomActuators). Use this so Network Details room/group table
 * shows the same sensor/actuator counts as the entity's detail page.
 */
export const countSensorActuatorInstancesByTag = (
    sensors: any[],
    actuators: any[],
    tagUIDs: Set<string>,
    tagUIDSAIDs: Map<string, Set<number>>,
    uidToDeviceMap: Map<string, any>
): { sensors: number; controllers: number } => {
    return {
        sensors: countSensorInstancesByTag(sensors, tagUIDs, tagUIDSAIDs, uidToDeviceMap),
        controllers: countActuatorInstancesByTag(actuators, tagUIDs, tagUIDSAIDs, uidToDeviceMap),
    };
};

export const shouldExcludeFromActuatorList = (deviceOrObjId: any, objId?: string | number): boolean => {
    let device: any = null;
    let objValue: string | number | undefined;
    if (deviceOrObjId && typeof deviceOrObjId === "object" && !Array.isArray(deviceOrObjId)) {
        device = deviceOrObjId;
        objValue = device?.OBJ ?? objId;
    } else {
        objValue = deviceOrObjId ?? objId;
    }
    if (device) {
        const name = device?.NAME;
        if (isRoomOrZoneByName(name) || isGroupOrHubByName(name)) return true;
    }
    if (objValue === null || objValue === undefined) return false;
    const numericValue = parseObjValue(objValue);
    if (Number.isNaN(numericValue)) return false;
    if (numericValue === 45058) return true;
    if (numericValue === 45057) return true;
    if (isAuxiliaryHub(numericValue)) return true;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (isSensor(numericValue)) return true;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (isMeter(numericValue)) return true;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    return false;
};

const shouldExcludeDeviceFromCount = (device: any): boolean => {
    const name = device?.NAME;
    const nick = device?.NICK;
    const nameLower = name ? String(name).toLowerCase().trim() : "";
    const nickLower = nick ? String(nick).toLowerCase().trim() : "";
    if ((nameLower === "zone" || nickLower === "zone") && nameLower !== "room" && nickLower !== "room") {
        return true;
    }
    if (isGroupOrHubByName(name)) return true;
    const obj = device?.OBJ;
    // Do not exclude explicit room devices from counts, even if their OBJ
    // would otherwise match auxiliary hub / meter heuristics.
    if (!isRoomByName(name, nick) && (isAuxiliaryHub(obj) || isMeter(obj))) return true;
    return false;
};

const getDeviceTypeForCount = (device: any): "room" | "sensor" | "controller" | null => {
    const name = device?.NAME;
    const nick = device?.NICK;
    if (isRoomByName(name, nick)) return "room";
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    const obj = device?.OBJ;
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (isSensor(obj)) return "sensor";
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (obj === "45058" || obj === 45058) return "room";
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    if (containsActuatorObject(obj)) return "controller";
    /* istanbul ignore next -- unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
    return null;
};

export const countDevicesByType = (
    uids: Set<string>,
    devices: any[]
): { sensors: number; controllers: number; rooms: number } => {
    let sensorCount = 0;
    let controllerCount = 0;
    let roomCount = 0;
    for (const device of devices) {
        const deviceUID = device?.UID ?? device?.uid ?? "";
        if (!deviceUID || !uids.has(String(deviceUID))) continue;
        if (shouldExcludeDeviceFromCount(device)) continue;
        const deviceType = getDeviceTypeForCount(device);
        /* istanbul ignore else -- other device types unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
        if (deviceType === "room") roomCount++;
        else if (deviceType === "sensor") sensorCount++;
        else if (deviceType === "controller") controllerCount++;
    }
    return { sensors: sensorCount, controllers: controllerCount, rooms: roomCount };
};

export const countDevicesByTagUIDSAIDs = (
    uidSaidsMap: Map<string, Set<number>>,
    devices: any[]
): { sensors: number; controllers: number; rooms: number } => {
    let sensorCount = 0;
    let controllerCount = 0;
    let roomCount = 0;
    for (const [uid, saids] of Array.from(uidSaidsMap.entries())) {
        const device = devices.find((d: any) => {
            const deviceUID = d?.UID || d?.uid || "";
            return String(deviceUID) === uid;
        });
        if (!device) continue;
        if (shouldExcludeDeviceFromCount(device)) continue;
        const deviceType = getDeviceTypeForCount(device);
        const saidCount = saids && saids.size > 0 ? saids.size : 1;
        /* istanbul ignore else -- other device types unreachable while isAuxiliaryHub() treats any numeric OBJ as auxiliary hub */
        if (deviceType === "room") roomCount++;
        else if (deviceType === "sensor") sensorCount += saidCount;
        else if (deviceType === "controller") controllerCount += saidCount;
    }
    return { sensors: sensorCount, controllers: controllerCount, rooms: roomCount };
};

export const getTagDeviceUID = (device: any): string | null => {
    if (!Array.isArray(device) || device.length === 0) return null;
    const uidCandidate = device.length > 1 && device[1] ? device[1] : device[0];
    return uidCandidate ? String(uidCandidate) : null;
};

export const getTagDeviceSAID = (device: any): number => {
    if (!Array.isArray(device) || device.length === 0) return 0;
    const saidIndex = device.length >= 3 ? 2 : 1;
    const saidValue = device[saidIndex];
    return saidValue !== undefined && saidValue !== null ? Number(saidValue) : 0;
};

/**
 * Helper to add a device's UID–SAID pair into a UID→Set<SAID> map.
 * Shared between NetworkDetails, NetworksList, and ZoneDetails.
 */
export const addDeviceToUIDSAIDsMap = (
    device: any,
    uidSaidMap: Map<string, Set<number>>
): void => {
    const uid = getTagDeviceUID(device);
    if (!uid) {
        return;
    }
    const said = getTagDeviceSAID(device);
    if (!uidSaidMap.has(uid)) {
        uidSaidMap.set(uid, new Set<number>());
    }
    uidSaidMap.get(uid)!.add(said);
};

const isDeviceEntityOrRoomZone = (device: any): boolean => {
    const name = device?.NAME;
    const nick = device?.NICK;
    return (
        isGroupOrHubByName(name) ||
        isGroupOrHubByName(nick) ||
        isRoomOrZoneByName(name) ||
        isRoomOrZoneByName(nick)
    );
};

const buildConfigSensorEntry = (
    uid: string,
    said: number,
    sensorName: string,
    configName: string,
    deviceObj: any
): any => ({
    ldsu_uuid: uid,
    UID: uid,
    ldsu_name: configName,
    SAID: said,
    sensor_name: sensorName,
    obj: deviceObj,
    unit: "-",
    minmax: ["-", "-"],
    accuracy: 2,
});

/**
 * Build sensor-like entries from registry config for (UID, SAID) pairs in the zone tag.
 * Used when getLDSBusInfo does not return a device (e.g. missing from LDSU status) so
 * Zone Details can still show sensors that are in the zone tag and in registry config.
 */
export const getSensorsFromConfigForZoneTag = (
    zoneTagUIDSAIDs: Map<string, Set<number>>,
    devices: any[],
    configList: any[]
): any[] => {
    const out: any[] = [];
    if (!zoneTagUIDSAIDs?.size || !Array.isArray(devices) || !Array.isArray(configList)) {
        return out;
    }
    const entries = Array.from(zoneTagUIDSAIDs.entries());
    entries.forEach(([uid, saidSet]) => {
        const device = devices.find((d: any) => String(d?.UID ?? d?.uid ?? "") === String(uid));
        if (!device || isDeviceEntityOrRoomZone(device)) return;

        const config = configList.find((c: any) => String(c?.UID ?? "") === String(uid));
        if (!config || !Array.isArray(config.SAIDS) || !config.SAIDS.length) return;
        const sensorNames = config.sensor_names;
        const configName = config.name ?? device?.NAME ?? "";

        Array.from(saidSet).forEach((said) => {
            const idx = config.SAIDS.indexOf(said);
            if (idx === -1) return;
            // IO Controller outputs (actuators) typically have non‑empty CMDS objects.
            // Skip such channels here so they are not treated as sensors in ZoneDetails.
            const cmdsArray = Array.isArray(config.CMDS) ? config.CMDS : [];
            const cmdForSaid = cmdsArray[idx];
            if (cmdForSaid && !isEmpty(cmdForSaid)) {
                return;
            }
            const sensorName =
                Array.isArray(sensorNames) && sensorNames[idx] != null
                    ? sensorNames[idx]
                    : configName || "Sensor";
            out.push(buildConfigSensorEntry(uid, said, sensorName, configName, device?.OBJ));
        });
    });
    return out;
};

/**
 * Helper to check if a set of UIDs corresponds to a zone entity (not a room)
 * based on registry devices' NAME/NICK fields.
 * Used to detect \"room\" tags that actually refer to zones.
 */
export const isUIDSetZoneNotRoom = (
    uids: Set<string>,
    devices: any[]
): boolean => {
    for (const uid of Array.from(uids)) {
        const device = devices.find((d: any) => {
            const deviceUID = d?.UID || d?.uid || "";
            return String(deviceUID) === uid;
        });

        if (device) {
            const name = device?.NAME;
            const nick = device?.NICK;
            const nameLower = name ? String(name).toLowerCase().trim() : "";
            const nickLower = nick ? String(nick).toLowerCase().trim() : "";

            // If the primary device is a zone (not a room), treat this UID set as zone
            if (
                (nameLower === "zone" || nickLower === "zone") &&
                nameLower !== "room" &&
                nickLower !== "room"
            ) {
                return true;
            }
        }
    }
    return false;
};

export const createHandleSensorTimeRangeChange = (
    setTimeRange: (value: TimeRangeSelection) => void,
    refreshAllSensorReadings: (lastTime: string, offsetTime?: string) => Promise<void>,
    refreshAllActuatorReadings?: (lastTime: string, offsetTime?: string) => Promise<void>
) => {
    return async ({ range, startDate, endDate }: TimeRangeSelection) => {
        let lastTimeValue = Math.round(moment.duration(endDate.diff(startDate)).asSeconds());
        let offsetTime: string | undefined;
        if (range === CHART_CUSTOM_RANGE_OPTION) {
            lastTimeValue = Math.round(moment.duration(moment().diff(endDate)).asSeconds());
            offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
            lastTimeValue = moment.duration(endDate.diff(startDate)).asSeconds();
        }
        const lastTime = convertSecondsToPrometheusTime(lastTimeValue);
        setTimeRange({ range, startDate, endDate });
        if (refreshAllActuatorReadings) {
            await Promise.all([
                refreshAllSensorReadings(lastTime, offsetTime),
                refreshAllActuatorReadings(lastTime, offsetTime),
            ]);
        } else {
            await refreshAllSensorReadings(lastTime, offsetTime);
        }
    };
};

// --- IO controller actuators (from config when missing from getLDSBusInfo) ---
const ACTUATOR_CLS_MIN = 32768;

function getSchemasWithActuators(jsonList: any[]): any[] {
    return jsonList.filter((j: any) => {
        if (!j.SNS || j.OBJ === "256") return false;
        return j.SNS.some((s: any) => Number(s.CLS) >= ACTUATOR_CLS_MIN);
    });
}

function getDevicesToProcess(
    registryDevices: any[],
    configList: any[],
    busNumber: string
): { device: any; fromApp: boolean }[] {
    const withApp = registryDevices.filter(
        (d: any) =>
            containsActuatorObject(d?.OBJ) && d?.APP && String(d?.PORT) === String(busNumber)
    );
    const noApp = registryDevices.filter((d: any) => {
        if (d?.APP || String(d?.PORT) !== String(busNumber)) return false;
        const cfg = configList.find((c: any) => String(c?.UID) === String(d.UID));
        return (cfg?.SAIDS?.length ?? 0) >= 4;
    });
    return [
        ...withApp.map((d: any) => ({ device: d, fromApp: true })),
        ...noApp.map((d: any) => ({ device: d, fromApp: false })),
    ];
}

function shouldIncludeSchema(schema: any, fromApp: boolean): boolean {
    if (fromApp) return true;
    const hasInput = schema.SNS?.some((s: any) => Number(s?.CLS) < ACTUATOR_CLS_MIN);
    const hasOutput = schema.SNS?.some((s: any) => Number(s?.CLS) >= ACTUATOR_CLS_MIN);
    return !!(hasInput && hasOutput);
}

function collectOutputChannelIndices(
    schema: any,
    config: any,
    device: any,
    fromApp: boolean
): { index: number; type: string; appId: string | undefined }[] {
    const indices: { index: number; type: string; appId: string | undefined }[] = [];
    const snsLen = schema.SNS?.length ?? 0;
    for (let k = 0; k < snsLen; k++) {
        const isOutput = Number(schema.SNS[k]?.CLS) >= ACTUATOR_CLS_MIN;
        const hasCmd = !isEmpty(config.CMDS?.[k]);
        const notFromRegistry = fromApp ? isEmpty(device.APP?.[k]) : true;
        if (!isOutput || !hasCmd || !notFromRegistry) continue;
        indices.push({
            index: k,
            type: get(clsToAttributeTable(), schema.SNS[k]?.CLS) || "IO Controller Output",
            appId: undefined,
        });
    }
    return indices;
}

function buildActuatorEntries(
    base: any,
    actuatorIndex: { index: number; type: string; appId: string | undefined }[],
    schema: any
): any[] {
    const entries: any[] = [];
    const CMDS: any[] = [];
    for (const item of actuatorIndex) {
        CMDS.push(base.CMDS?.[item.index]);
        entries.push({
            ...base,
            CMDS: [...CMDS],
            name: base.sensor_name?.[item.index],
            type: item.type,
            info: base.CMDS?.[item.index],
            said: item.index,
            CLS: schema.SNS[item.index]?.CLS,
            appId: item.appId,
        });
    }
    return entries;
}

/**
 * Build actuator entries for IO controller output channels that have CMDS in config
 * but are missing from getLDSBusInfo (e.g. no status device match).
 */
export function getIOControllerActuatorsFromConfig(
    busNumber: string,
    registryDevices: any[],
    configList: any[],
    statusDevices: any[]
): any[] {
    const jsonList = localStorageService.getItem("listOfJSON") || [];
    const filteredJsonList = getSchemasWithActuators(jsonList);
    const devicesToProcess = getDevicesToProcess(registryDevices, configList, busNumber);
    const result: any[] = [];

    for (const { device, fromApp } of devicesToProcess) {
        const config = configList.find((c: any) => String(c?.UID) === String(device.UID));
        if (!config?.CMDS) continue;

        const statusDevice = statusDevices?.find((s: any) => String(s?.UID) === String(device.UID));
        const base = {
            UID: device.UID,
            OBJ: device.OBJ,
            SAID: config.SAIDS,
            sensor_name: config.sensor_names,
            CMDS: config.CMDS,
            timestamp: statusDevice?.ts,
            status: statusDevice?.value,
            APP: device.APP,
        };

        for (const schema of filteredJsonList) {
            if (String(schema.OBJ) !== String(device.OBJ)) continue;
            if (!shouldIncludeSchema(schema, fromApp)) continue;

            const actuatorIndex = collectOutputChannelIndices(schema, config, device, fromApp);
            result.push(...buildActuatorEntries(base, actuatorIndex, schema));
        }
    }

    return result;
}

/**
 * Derive the primary entity UUID from the intersection of tag UIDs and
 * registry devices, reproducing the logic used for hubs and reusing it
 * for zones, rooms, and groups.
 *
 * Heuristic:
 * - Only consider UIDs that exist in the registry (candidateUIDs already filtered)
 * - Prefer a device whose NICK matches the entity name (route/tag name)
 * - Then prefer a device whose NAME matches the expected type ("hub"/"zone"/"room"/"group")
 * - Fallback to the first UID in the set
 */
export function selectEntityUUID(
    candidateUIDs: Set<string>,
    devices: any[],
    entityName: string,
    entityType: "hub" | "zone" | "room" | "group"
): string | null {
    const uidList = Array.from(candidateUIDs);
    if (!uidList.length) {
        return null;
    }

    const candidates = uidList
        .map((uid) => ({
            uid,
            device: devices.find(
                (d: any) => String(d?.UID ?? d?.uid ?? "") === String(uid)
            ),
        }))
        .filter(({ device }) => !!device);

    if (!candidates.length) {
        return uidList[0] ?? null;
    }

    // 1) Prefer device whose NICK matches the logical name (zoneId/roomId/groupId/hubId),
    //    with light normalisation so that "sensor_zone_1" matches "sensor zone 1".
    const nickMatch = candidates.find(({ device }) => {
        const nick = String(device?.NICK || device?.nick || "").trim();
        const normalisedNick = nick.toLowerCase();
        const normalisedEntityName = String(entityName)
            .trim()
            .toLowerCase()
            .replaceAll("_", " ");

        return normalisedNick === normalisedEntityName;
    });
    if (nickMatch) {
        return nickMatch.uid;
    }

    // 2) Prefer device whose NAME matches the entity type ("hub"/"zone"/"room"/"group")
    const typeMatch = candidates.find(({ device }) => {
        const name = String(device?.NAME || device?.name || "")
            .trim()
            .toLowerCase();
        return name === entityType;
    });
    if (typeMatch) {
        return typeMatch.uid;
    }

    // 3) Fallback: first UID in the set
    return uidList[0] ?? null;
}
